
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Card, Button, Alert, Form } from "react-bootstrap";
import { IconCaretLeft } from "@tabler/icons-react";
import { Helmet } from "react-helmet-async";

const normalizeURL = (url) => {
    try {
        const parsedUrl = new URL(url.trim());
        const hostname = parsedUrl.hostname.toLowerCase();
        return parsedUrl.protocol + "//" + hostname + parsedUrl.pathname + parsedUrl.search;
    } catch {
        return null;
    }
};

const AddProject = () => {
    const [name, setName] = useState("");
    const [description, setDescription] = useState("");
    const [links, setLinks] = useState("");
    const [file, setFile] = useState(null);
    const [error, setError] = useState(null);
    const [successMessage, setSuccessMessage] = useState("");
    const [userCredits, setUserCredits] = useState(0);
    const [creditsUsed, setCreditsUsed] = useState(0);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const navigate = useNavigate();

    useEffect(() => {
        const fetchCredits = async () => {
            try {
                const token = localStorage.getItem("token");
                const response = await fetch("/api/auth/profile", {
                    headers: { Authorization: `Bearer ${token}` },
                });
                if (response.ok) {
                    const data = await response.json();
                    setUserCredits(data.credits || 0);
                }
            } catch (err) {
                console.error("Error fetching balance:", err);
            }
        };
        fetchCredits();
    }, []);

    useEffect(() => {
        const countCredits = async () => {
            try {
                let linkArray = links.split(/\r?\n/).map(normalizeURL).filter(Boolean);
                if (file) {
                    const fileText = await file.text();
                    const fileLinks = fileText.split(/\r?\n/).map(normalizeURL).filter(Boolean);
                    linkArray = [...linkArray, ...fileLinks];
                }
                const uniqueLinks = [...new Set(linkArray)];
                setCreditsUsed(uniqueLinks.length);
            } catch (e) {
                console.error("Error counting links:", e);
            }
        };
        countCredits();
    }, [links, file]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);
        setError(null);
        setSuccessMessage("");

        if (!name.trim()) {
            setError("Please enter the project name!");
            setIsSubmitting(false);
            return;
        }

        let linkArray = links.split(/\r?\n/).map(normalizeURL).filter(Boolean);

        if (file) {
            try {
                const fileText = await file.text();
                const fileLinks = fileText.split(/\r?\n/).map(normalizeURL).filter(Boolean);
                linkArray = [...linkArray, ...fileLinks];
            } catch (error) {
                setError("Error reading file");
                setIsSubmitting(false);
                return;
            }
        }

        const uniqueLinks = [...new Set(linkArray)];

        if (uniqueLinks.length > 2000) {
            setError(`Limit exceeded: maximum 2000 links allowed. You entered ${uniqueLinks.length}.`);
            setIsSubmitting(false);
            return;
        }

        if (uniqueLinks.length > userCredits) {
            setError({
                text: `Not enough credits. Required: ${uniqueLinks.length}, available: ${userCredits}.`,
                link: true
            });
            setIsSubmitting(false);
            return;
        }

        try {
            const token = localStorage.getItem("token");
            const formData = new FormData();
            formData.append("name", name.trim());
            formData.append("description", description.trim());
            formData.append("links", uniqueLinks.join("\n"));

            const response = await fetch("/api/projects", {
                method: "POST",
                headers: { Authorization: `Bearer ${token}` },
                body: formData,
            });

            if (!response.ok) {
                const errorData = await response.json().catch(() => null);
                const message = errorData?.detail || "Error creating project.";
                setError(message);
                return;
            }

            setSuccessMessage("Project created successfully, links have been sent for indexing!");
            setTimeout(() => navigate("/"), 2000);
        } catch (error) {
            console.error("Error creating project:", error);
            setError(error.message);
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
<><Helmet> <title>Add Project - IndexBooster</title></Helmet>
        <Card>
            <Card.Header className="d-flex align-items-center gap-2">
                <Button variant="light" size="sm" onClick={() => navigate(-1)}>
                    <IconCaretLeft size={18} />
                </Button>
                <h3 className="card-title mb-0">Create Project</h3>
            </Card.Header>
            <Card.Body>
                {error && (
                    <Alert variant="danger">
                        {typeof error === "string" ? error : (
                            <>
                                {error.text} <a href="/buy-credits">Top up balance</a>
                            </>
                        )}
                    </Alert>
                )}
                {successMessage && <Alert variant="success">{successMessage}</Alert>}
                <Form onSubmit={handleSubmit}>
                    <Form.Group className="mb-3">
                        <Form.Label>Project Name*</Form.Label>
                        <Form.Control type="text" value={name} onChange={(e) => setName(e.target.value)} required />
                    </Form.Group>
                    <Form.Group className="mb-3">
                        <Form.Label>Description</Form.Label>
                        <Form.Control as="textarea" value={description} onChange={(e) => setDescription(e.target.value)} />
                    </Form.Group>
                    <Form.Group className="mb-3">
                        <Form.Label>List of Links</Form.Label>
                        <Form.Control as="textarea" rows="5" value={links} onChange={(e) => setLinks(e.target.value)} />
                        <Form.Text className={creditsUsed > userCredits ? "text-danger fw-bold" : "text-muted"}>
                            Credits use: {creditsUsed}/{userCredits}
                        </Form.Text>
                    </Form.Group>
                    <Form.Group className="mb-3">
                        <Form.Label>Upload file (CSV/TXT)</Form.Label>
                        <Form.Control type="file" accept=".csv,.txt" onChange={(e) => setFile(e.target.files[0])} />
                    </Form.Group>
                    <Button variant="primary" type="submit" disabled={isSubmitting}>
                        {isSubmitting ? (
                            <>
                                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true" />
                                Creating Project...
                            </>
                        ) : (
                            "Add Project"
                        )}
                    </Button>
                </Form>
            </Card.Body>
        </Card>
  </>  );
};

export default AddProject;
